import React, { useMemo } from "react";
import { Alert, Table } from "reactstrap";
import { EDocumentAtvStatus } from "./index";

type ErrorRecord = {
  code: number;
  message: string;
  row: number;
  column: number;
};

type Props = {
  text: string;
  documentStatus?: EDocumentAtvStatus;
};

/**
 * Hacienda devuelve el motivo de rechazo en dos formatos: una tabla
 * (codigo, mensaje, fila, columna) para errores de esquema XSD, o texto libre
 * para el resto (firma invalida, certificado vencido, clave duplicada). Ambos
 * deben mostrarse; si el detalle no se entiende sin contexto tecnico se agrega
 * una explicacion en lenguaje llano.
 */
const USER_HINTS: { pattern: RegExp; hint: string }[] = [
  {
    pattern: /CodigoPais/i,
    hint:
      "Al cliente del documento le falta el codigo de pais del telefono, asi que la etiqueta salio vacia y Hacienda rechaza el comprobante por esquema. Corrijalo en la ficha del cliente (para Costa Rica es 506) y vuelva a emitir.",
  },
  {
    pattern: /NumTelefono/i,
    hint:
      "El telefono del cliente esta vacio o tiene menos de 3 digitos. Hacienda exige un numero valido en el receptor del comprobante.",
  },
  {
    pattern: /(No Sujet[ao]s?|mercanc[íi]as exentas|servicios exentos|gravad[ao]s?).*(no coincide|no corresponde)/i,
    hint:
      "Los totales por tipo de mercancia o servicio no le cuadran a Hacienda. Hacienda clasifica cada linea por el primer digito de su CAByS (6, 7, 8 y 9 son servicios) y por la tarifa con que se facturo, asi que basta un producto con el CAByS o la tarifa mal configurados. Revise el detalle de lineas que aparece abajo.",
  },
  {
    pattern: /firma del comprobante.*no es v[áa]lida/i,
    hint:
      "El certificado digital (.p12) con el que se firmo el documento no corresponde al ambiente de Hacienda configurado (pruebas o produccion), o su contraseña es incorrecta. Revise el certificado y el ambiente en la configuracion de facturacion electronica.",
  },
  {
    pattern: /(certificado|llave).*(vencid|expirad)/i,
    hint:
      "El certificado digital esta vencido. Debe renovarlo en el ATV y volver a cargarlo en la configuracion de facturacion electronica.",
  },
  {
    pattern: /clave.*(duplicad|ya (fue )?(recibid|registrad))/i,
    hint:
      "Hacienda ya habia recibido un documento con esta misma clave. Verifique la numeracion consecutiva antes de reenviarlo.",
  },
];

const ErrorsViewer: React.FC<Props> = ({ text, documentStatus }) => {
  const { intro, rows, details } = useMemo(() => parseErrorBlock(text), [text]);

  const hasErrorDetail = rows.length > 0 || details.length > 0;
  const isRejected = documentStatus === "rechazado";
  const showAsError = hasErrorDetail || isRejected;

  const hints = useMemo(() => {
    const source = [...details, ...rows.map((r) => r.message)].join(" ");
    return USER_HINTS.filter(({ pattern }) => pattern.test(source)).map(({ hint }) => hint);
  }, [details, rows]);

  return (
    <div>
      {intro && (
        <Alert color={showAsError ? "danger" : "info"} className="mb-3" style={{ whiteSpace: "pre-line" }}>
          {intro}
        </Alert>
      )}

      {hints.map((hint, i) => (
        <Alert key={`hint-${i}`} color="warning" className="mb-3">
          <b>Que significa:</b> {hint}
        </Alert>
      ))}

      {rows.length > 0 && (
        <Table bordered responsive size="sm">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Código</th>
              <th>Mensaje</th>
              <th style={{ width: 100 }}>Fila</th>
              <th style={{ width: 110 }}>Columna</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.code}</td>
                <td>{r.message}</td>
                <td>{r.row}</td>
                <td>{r.column}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {rows.length === 0 && details.length > 0 && (
        <Alert color="danger" style={{ whiteSpace: "pre-line" }}>
          {details.join("\n")}
        </Alert>
      )}

      {!hasErrorDetail && (
        isRejected ? (
          <Alert color="danger">
            Hacienda rechazo el documento pero no detallo el motivo. Reintente el envio; si el
            rechazo persiste comuniquese con soporte.
          </Alert>
        ) : (
          <Alert color="success">No se encontraron errores.</Alert>
        )
      )}
    </div>
  );
};

export default ErrorsViewer;

/** ================== Parser con RegEx ================== **/

/**
 * Estructura esperada:
 * Texto libre ... [ codigo, mensaje, fila, columna -476, ""mensaje..."", 0, 0 -45, ""mensaje..."", 0, 0 ... ]
 * - El header (codigo, mensaje, fila, columna) puede estar presente o no.
 * - Mensajes van entre comillas dobles dobles: ""..."" y pueden contener comas.
 * - Si el contenido entre corchetes no sigue ese formato se devuelve como
 *   `details` (texto libre), nunca se descarta.
 */
function parseErrorBlock(input: string): { intro: string; rows: ErrorRecord[]; details: string[] } {
  const text = `${input ?? ""}`;
  // Greedy a proposito: el mensaje de firma trae corchetes anidados
  // ("[STAGING o PRODUCCION]") y con match perezoso se cortaria a la mitad.
  const bracketMatch = text.match(/\[(.*)\]/s);
  const rows: ErrorRecord[] = [];

  // Intro = todo lo que queda fuera de los corchetes (antes y después), preservado
  const intro = text.replace(/\[.*\]/s, "").trim();

  if (!bracketMatch) {
    return { intro, rows, details: [] };
  }

  let inner = bracketMatch[1].trim();

  // Remueve encabezado opcional al inicio: "codigo, mensaje, fila, columna"
  inner = inner.replace(/^\s*codigo\s*,\s*mensaje\s*,\s*fila\s*,\s*columna\s*/i, "").trim();

  /**
   * RegEx para capturar registros en cuartetos:
   *  (-?\d+)                     -> código (número, puede ser negativo)
   *  ,\s*""([\s\S]*?)""          -> mensaje entre comillas dobles dobles (permite comas dentro)
   *  ,\s*(\d+)                   -> fila
   *  ,\s*(\d+)                   -> columna
   * Flags: g (global), s (dotAll) para que [\s\S]*? funcione con saltos de línea
   */
  const rowRegex = /(-?\d+)\s*,\s*""([\s\S]*?)""\s*,\s*(\d+)\s*,\s*(\d+)/gs;
  let m: RegExpExecArray | null;

  while ((m = rowRegex.exec(inner)) !== null) {
    const code = Number(m[1]);
    const message = m[2].trim(); // ya sin las "" dobles
    const row = Number(m[3]);
    const column = Number(m[4]);

    if (Number.isFinite(code) && Number.isFinite(row) && Number.isFinite(column)) {
      rows.push({ code, message, row, column });
    }
  }

  // Sin formato tabular: el contenido es el motivo en texto libre y debe mostrarse igual.
  const details = rows.length === 0
    ? inner.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
    : [];

  return { intro, rows, details };
}
